import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState,
} from 'react'
import { Link, useHistory } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { stringify } from 'query-string'
import { decode } from 'he'
import classnames from 'classnames'
import accounting from 'accounting'
import { pick, isEmpty, get } from 'lodash'

import { PageHeading } from '../../../components'
import ReportActions from '../../../components/ReportActions'
import Table, { Col } from '../../../components/Table/next'
import { PerformanceTableContext } from './index'
import PerformanceFilters from './PerformanceFilters'

import { State } from '../../../reducers'
import { exportReport, getReport } from '../../../actions/reports'
import { pruneObject } from '../../../utils/objectManagement'

import { DataStatus } from '../../../typings/status'
import { ReportData, ReportStatus, Transaction } from '../../../typings/report'
import { ReportsStore } from '../../../typings/stores/ReportsStore'

import styles from './table.module.css'

interface TableRow {
  id?: number
  clicks: number
  applications: number
  sales: number
  commission: number
  productTypeId?: number
  productId?: number
  advertiserId?: number
  subtracking?: string | number
  categoryId?: number
  name?: string
}

export interface Status extends DataStatus {
  loading: boolean
  hasError: boolean
}

const PerformanceTable: FunctionComponent = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const [showData, setShowData] = useState<boolean>(true)
  const report = useSelector<State, ReportsStore>(state => state.reports)

  const reportStatus = useSelector<State, ReportStatus>(state => ({
    ...pick(state.reports, ['hasData', 'status', 'error']),
    loading: state.reports.status === 'fetching',
    hasError: !isEmpty(state.reports.error),
  }))
  const exportStatus = useSelector<State, ReportStatus>(state => ({
    ...pick(state.reports.export, ['hasData', 'status', 'error']),
    loading: state.reports.export.status === 'fetching',
    hasError: !isEmpty(state.reports.export.error),
  }))

  const {
    changePage,
    changePageSize,
    changeSort,
    filters,
    pagination: { size, page },
    sort,
  } = useContext(PerformanceTableContext)

  const fetchReport = () => {
    dispatch(
      getReport('performance', { ...filters, ...pruneObject(sort), size, page })
    )

    /**
     * report.hasData is set to false when the dispatch above is called.
     * Because hasData is false, we can reset showData and data will remain hidden since both need to be true for data to render.
     *
     * This is just prepping for the upcoming report
     */
    setShowData(true)
  }
  const fetchExport = () =>
    dispatch(
      exportReport('performance', {
        ...filters,
        ...pruneObject(sort),
        size,
        page,
      })
    )

  /**
   * When filters.groupBy changes, the table cols are set to the cols needed by the new grouping.
   * We need to hide the data, so that the old data doesn't end up in the new cols.
   */
  useEffect(() => {
    setShowData(false)
  }, [filters.groupBy])

  useEffect(() => {
    fetchReport()
  }, [size, page, sort])

  const generateDetailReportLink = (
    id: string | number,
    value: string | number,
    detailPath: string
  ) => {
    const { dateType, startDate, endDate, groupBy } = filters

    const detailParams = {
      [groupBy]: id,
      dateType,
      startDate,
      endDate,
    }

    return parseInt(value.toString(), 10) > 0 ? (
      <Link
        to={{
          pathname: detailPath,
          search: `?${stringify(detailParams)}`,
          state: {
            performanceReportUrl: `${history.location.pathname}${history.location.search}`,
          },
        }}
        className={styles.link}
      >
        {value}
      </Link>
    ) : (
      <div className={styles.link}>{value}</div>
    )
  }

  const commonFields = (t: Transaction) => ({
    clicks: t.clicks,
    applications: t.applications,
    sales: t.sales,
    commission: t.commissions,
  })

  let cols: Col<TableRow[]>[] = [
    {
      text: 'Clicks',
      sortable: true,
      field: 'clicks',
      className: classnames(styles.fill, styles.center),
      render: row =>
        generateDetailReportLink(
          row.id,
          row.clicks,
          './reports/details/clicks'
        ),
    },
    {
      text: 'Applications',
      sortable: true,
      field: 'applications',
      className: classnames(styles.fill, styles.center),
      render: row =>
        generateDetailReportLink(
          row.id,
          row.applications,
          './reports/details/applications'
        ),
    },
    {
      text: 'Sales',
      sortable: true,
      field: 'sales',
      className: classnames(styles.fill, styles.center),
      render: row =>
        generateDetailReportLink(row.id, row.sales, './reports/details/sales'),
    },
    {
      text: 'Commission',
      sortable: true,
      field: 'commission',
      className: styles.right,
      render: row => accounting.formatMoney(row.commission, '$'),
    },
  ]

  const reportData: ReportData = get(report, `data[${filters.dateType}]`, {})
  const totals = reportData.totals || null
  const transactions = reportData.transactions || []

  let data: TableRow[] = []
  /**
   * I'm sure there's a nicer way of doing this...
   */
  switch (filters.groupBy) {
    case 'productType':
      cols = [
        { text: 'Product Type ID', field: 'productTypeId' },
        {
          text: 'Name',
          sortable: true,
          field: 'name',
          render: row => decode(row.name),
        },
        ...cols,
      ]

      data = transactions.map(t => ({
        id: t.id, // we need a non-specific id for detailed reports
        productTypeId: t.id,
        name: t.name || '',
        ...commonFields(t),
      }))
      break
    case 'product':
      cols = [
        { text: 'Product ID', field: 'productId' },
        {
          text: 'Name',
          sortable: true,
          field: 'name',
          render: row => decode(row.name),
        },
        ...cols,
      ]

      data = transactions.map(t => ({
        id: t.id,
        productId: t.id,
        name: t.name || '',
        ...commonFields(t),
      }))
      break
    case 'advertiser':
      cols = [
        { text: 'Advertiser ID', field: 'advertiserId' },
        {
          text: 'Name',
          sortable: true,
          field: 'name',
          render: row => decode(row.name),
        },
        ...cols,
      ]

      data = transactions.map(t => ({
        id: t.id,
        advertiserId: t.id,
        name: t.name || '',
        ...commonFields(t),
      }))
      break
    case 'category':
      cols = [
        { text: 'Category ID', field: 'categoryId' },
        {
          text: 'Name',
          sortable: true,
          field: 'name',
          render: row => decode(row.name),
        },
        ...cols,
      ]

      data = transactions.map(t => ({
        id: t.id,
        categoryId: t.id,
        name: t.name || '',
        ...commonFields(t),
      }))
      break
    case 'subtracking':
      cols = [{ text: 'Variable', field: 'subtracking' }, ...cols]

      data = transactions.map(t => ({
        id: t.id,
        subtracking: t.id,
        ...commonFields(t),
      }))
      break
  }

  const pagination = {
    pageSize: get(report, `data[${filters.dateType}].pagination.pageSize`, 0),
    total: get(report, `data[${filters.dateType}].pagination.total`, 0),
    currentPage: get(
      report,
      `data[${filters.dateType}].pagination.currentPage`,
      0
    ),
  }

  return (
    <>
      <PageHeading
        title="Performance Report"
        description="This report contains aggregated information for clicks, sales and applications filtered by a specified date range."
      >
        <PerformanceFilters />
      </PageHeading>
      <ReportActions
        fetchReport={fetchReport}
        fetchExport={fetchExport}
        reportStatus={reportStatus}
        exportStatus={exportStatus}
      />
      <Table<TableRow[]>
        className={styles.table}
        totalRow={[
          {
            text: 'TOTALS',
            colSpan: cols.length - 4,
          },
          {
            text: totals && totals.clicks,
            className: styles.center,
          },
          {
            text: totals && totals.applications,
            className: styles.center,
          },
          {
            text: totals && totals.sales,
            className: styles.center,
          },
          {
            text: totals && accounting.formatMoney(totals.commissions, '$'),
            className: styles.right,
          },
        ]}
        data={reportStatus.hasData && showData && data}
        onSortChange={changeSort}
        sort={sort}
        cols={cols}
        isLoading={report.status === 'fetching'}
        pagination={{
          onPageSizeUpdate: changePageSize,
          onPaginate: changePage,
          ...pagination,
        }}
      />
    </>
  )
}

export default PerformanceTable
